import { useEffect } from 'react';
import {
  createBearerAuthorization,
  evaluateNewSurfaceDecision,
} from './UiSurfaceBoundary';
import { fetchUiSurfaceBootstrap } from './uiSurfaceBootstrap';
import {
  ENTRY_ATTEMPT_PREFIX,
  clearUiSurfaceEntryAttempt,
  hasUiSurfaceEntryAttempt,
  markUiSurfaceEntryAttempt,
} from './uiSurfaceEntryAttempt';
const ELIGIBLE_ROLES = new Set(['client_admin', 'staff', 'admin']);

const isMyPagePath = (value) => value === '/mypage' || value === '/app/mypage';

export default function UiSurfaceEntryRedirect({
  authenticatedUser,
  token,
  expectedReleaseId,
  currentPath,
  fetchImpl,
  timeoutMs = 5_000,
  locationObject = typeof window === 'undefined' ? null : window.location,
  sessionStorageObject = typeof window === 'undefined' ? null : window.sessionStorage,
}) {
  const userId = authenticatedUser?.id;
  const userRole = authenticatedUser?.role;

  useEffect(() => {
    if (!isMyPagePath(currentPath) || !userId || !ELIGIBLE_ROLES.has(userRole)) return undefined;
    const authorization = createBearerAuthorization(token);
    if (!authorization || !expectedReleaseId) return undefined;

    if (hasUiSurfaceEntryAttempt(sessionStorageObject, expectedReleaseId, userId)) return undefined;

    let active = true;
    fetchUiSurfaceBootstrap({
      authenticatedUser: { id: userId, role: userRole },
      authHeader: authorization,
      fetchImpl,
      timeoutMs,
    }).then((decision) => {
      if (!active) return;
      const evaluation = evaluateNewSurfaceDecision(decision, expectedReleaseId);
      if (!evaluation.allowed) {
        if (decision?.safeFallback === false && decision?.effectiveSurface === 'legacy') {
          clearUiSurfaceEntryAttempt(sessionStorageObject, expectedReleaseId, userId);
        }
        return;
      }

      // Mark the one-shot attempt before navigation. If the new boundary fails
      // and returns here, this suppresses an automatic redirect loop while the
      // old page remains fully usable.
      if (!markUiSurfaceEntryAttempt(sessionStorageObject, expectedReleaseId, userId)) return;
      try {
        locationObject?.assign?.(decision.routes.new);
      } catch (_error) {
        // Keep the attempt marker. A reload stays on the known-safe old page.
      }
    }).catch(() => {
      // Fail closed: MyPage is already mounted underneath this helper.
    });

    return () => { active = false; };
  }, [currentPath, expectedReleaseId, fetchImpl, locationObject, sessionStorageObject, timeoutMs, token, userId, userRole]);

  return null;
}

export { ENTRY_ATTEMPT_PREFIX, isMyPagePath };
